import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const TermsOfService = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 hover:bg-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToHome')}
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-slate-700 to-blue-600 text-white p-8 rounded-t-2xl">
            <h1 className="text-4xl font-bold mb-4">{t('legal.termsOfService.title')}</h1>
            <p className="text-lg opacity-90">
              {t('legal.termsOfService.lastUpdated')}
            </p>
          </div>

          <div className="bg-white p-8 rounded-b-2xl shadow-lg">
            <div className="prose max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.agreementToTerms.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.agreementToTerms.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.serviceDescription.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.serviceDescription.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.userAccounts.title')}
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.termsOfService.userAccounts.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.acceptableUse.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.termsOfService.acceptableUse.description')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.termsOfService.acceptableUse.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.contentOwnership.title')}
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.termsOfService.contentOwnership.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.subscriptionBilling.title')}
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.termsOfService.subscriptionBilling.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.termination.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.termination.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.disclaimers.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.disclaimers.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.limitationLiability.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.limitationLiability.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.governingLaw.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {t('legal.termsOfService.governingLaw.content')}
                </p>

              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.changesToTerms.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.changesToTerms.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.copyrightPolicy.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {t('legal.termsOfService.copyrightPolicy.content')}
                </p>
                <p className="text-gray-600 mb-4">
                  {t('legal.termsOfService.copyrightPolicy.description')}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  {(t('legal.termsOfService.copyrightPolicy.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">

                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.termsOfService.contactUs.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.termsOfService.contactUs.content')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
